import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useContext, useEffect, useRef, useCallback, useState } from "react";
import {
  Nudge,
  getNudgeByKey,
  getNudgesByLocation,
  dismissNudge,
  trackNudgeInteraction,
} from "./nudgeApi";
import { AppContext } from "../context/app";
import { useExperiment } from "../hooks/useExperiment";
import { useAnalytics } from "../components/providers/AnalyticsProvider";

const NUDGE_EVENT = {
  IMPRESSION: "Nudge Impression",
  CLICK: "Nudge Click",
  DISMISS: "Nudge Dismiss",
};

type UseNudgeReturn = {
  nudge: Nudge | null;
  trackClick: () => Promise<void>;
  trackDismiss: () => Promise<void>;
  hasTrackedImpression: boolean;
  isDismissed: boolean;
  /** Must NOT be named `ref` as React treats `ref` as a reserved prop and doesn't pass it to components (i.e. it's not a traditional prop) */
  trackingRef: (element: HTMLDivElement | null) => void;
  /** React Query state */
  isLoading: boolean;
  error: unknown;
};

// Base props that all nudge components should extend
export type BaseNudgeProps = {
  nudge: Nudge;
  onDismiss?: () => void;
  onClick?: () => void;
  trackingRef?: (element: HTMLDivElement | null) => void;
  /** Whether to show the dismiss button */
  dismissible?: boolean;
};

// Simplified options
type UseNudgeOptions = {
  /** Minimum time in viewport before tracking (ms). Default: 1000ms */
  minViewTime?: number;
  /** Threshold for intersection observer (0-1). Default: 0.5 (50% visible) */
  threshold?: number;
  /** Whether to track immediately on mount. Default: false */
  trackOnMount?: boolean;
  /** Whether to disable product analytics (Heap, Segment, etc.). Default: false */
  disableProductAnalytics?: boolean;
  /** React Query options */
  queryOptions?: Omit<UseQueryOptions<Nudge | null>, "queryKey" | "queryFn">;
};

const DEFAULT_NUDGE_QUERY_OPTIONS: Omit<
  UseQueryOptions<any>,
  "queryKey" | "queryFn"
> = {
  retry: 5,
  retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff (1s, 2s, 4s, max 30s)
  staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  refetchOnWindowFocus: false,
  refetchOnReconnect: true,
  refetchInterval: false,
};

/**
 * The hook provides everything you need to display a nudge and track interactions.
 *
 * @example
 * ```tsx
 * const { nudge, trackClick, trackDismiss, trackingRef } = useNudge('my-nudge-key');
 *
 * return (
 *   <Nudge
 *     nudge={nudge}
 *     onClick={trackClick}
 *     onDismiss={trackDismiss}
 *     trackingRef={trackingRef}
 *   />
 * );
 * ```
 */
export function useNudge(
  key: string,
  options: UseNudgeOptions = {},
): UseNudgeReturn {
  const { useExperimentIsOn } = useExperiment();
  const nudgesAreEnabled = useExperimentIsOn("nudges");
  const analytics = useAnalytics();
  const { client } = useContext(AppContext);
  const clientId = client?.uuid || client?.client_uuid;

  const {
    minViewTime = 1000,
    threshold = 0.5,
    trackOnMount = false,
    disableProductAnalytics = false,
    queryOptions = {},
  } = options;

  const [hasTrackedImpression, setHasTrackedImpression] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    data: nudge,
    isLoading,
    error,
  } = useQuery<Nudge | null>({
    queryKey: ["nudge", clientId, key],
    enabled: nudgesAreEnabled,
    queryFn: () => getNudgeByKey(key),
    ...DEFAULT_NUDGE_QUERY_OPTIONS,
    ...queryOptions,
  });

  const trackImpression = useCallback(async () => {
    if (hasTrackedImpression || !nudge?.key) return;

    try {
      await trackNudgeInteraction(nudge.key, "impression");
      setHasTrackedImpression(true);

      if (!disableProductAnalytics) {
        analytics.trackEvent(NUDGE_EVENT.IMPRESSION, {
          nudge_key: nudge.key,
        });
      }
    } catch (error) {
      console.warn("Failed to track nudge impression:", error);
    }
  }, [hasTrackedImpression, nudge?.key, disableProductAnalytics, analytics]);

  const trackClick = useCallback(async () => {
    if (!nudge?.key) return;

    try {
      await trackNudgeInteraction(nudge.key, "click");

      if (!disableProductAnalytics) {
        analytics.trackEvent(NUDGE_EVENT.CLICK, {
          nudge_key: nudge.key,
        });
      }
    } catch (error) {
      console.warn("Failed to track nudge click:", error);
    }
  }, [nudge?.key, disableProductAnalytics, analytics]);

  const trackDismiss = useCallback(async () => {
    if (!nudge?.key) return;

    try {
      await dismissNudge(nudge.key);
      setIsDismissed(true);

      if (!disableProductAnalytics) {
        analytics.trackEvent(NUDGE_EVENT.DISMISS, {
          nudge_key: nudge.key,
        });
      }
    } catch (error) {
      console.warn("Failed to track nudge dismiss:", error);
    }
  }, [nudge?.key, disableProductAnalytics, analytics]);

  const trackingRef = useCallback(
    (element: HTMLDivElement | null) => {
      elementRef.current = element;

      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (!element || hasTrackedImpression || trackOnMount || !nudge?.key) {
        return;
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;

          if (entry.isIntersecting) {
            timeoutRef.current = setTimeout(() => {
              trackImpression();
            }, minViewTime);
          } else {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
          }
        },
        { threshold },
      );

      observerRef.current.observe(element);
    },
    [
      hasTrackedImpression,
      trackOnMount,
      nudge?.key,
      minViewTime,
      threshold,
      trackImpression,
    ],
  );

  useEffect(() => {
    if (trackOnMount && !hasTrackedImpression && nudge?.key) {
      trackImpression();
    }
  }, [trackOnMount, hasTrackedImpression, nudge?.key, trackImpression]);

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    nudge,
    trackClick,
    trackDismiss,
    hasTrackedImpression,
    isDismissed,
    trackingRef,
    isLoading,
    error,
  };
}

export type NudgeWithTracking = {
  nudge: Nudge;
  trackClick: () => Promise<void>;
  trackDismiss: () => Promise<void>;
  trackingRef: (element: HTMLDivElement | null) => void;
  hasTrackedImpression: boolean;
  isDismissed: boolean;
};

type UseNudgeLocationReturn = {
  nudges: NudgeWithTracking[];
  /** React Query state */
  isLoading: boolean;
  error: unknown;
};

// Options for location-based nudges
type UseNudgeLocationOptions = {
  /** Minimum time in viewport before tracking (ms). Default: 1000ms */
  minViewTime?: number;
  /** Threshold for intersection observer (0-1). Default: 0.5 (50% visible) */
  threshold?: number;
  /** Whether to track immediately on mount. Default: false */
  trackOnMount?: boolean;
  /** Whether to disable product analytics (Heap, Segment, etc.). Default: false */
  disableProductAnalytics?: boolean;
  /** React Query options */
  queryOptions?: Omit<UseQueryOptions<Nudge[]>, "queryKey" | "queryFn">;
};

/**
 * Hook for fetching and tracking multiple nudges for a specific nudge location/container.
 * Each nudge gets its own tracking state and functions.
 *
 * @example
 * ```tsx
 * const { nudges } = useNudgeLocation('dashboard.sidebar');
 *
 * return (
 *   <div>
 *     {nudges.map(({ nudge, trackClick, trackDismiss, trackingRef }) => (
 *       <Nudge
 *         key={nudge.key}
 *         nudge={nudge}
 *         onClick={trackClick}
 *         onDismiss={trackDismiss}
 *         trackingRef={trackingRef}
 *       />
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useNudgeLocation(
  location: string,
  options: UseNudgeLocationOptions = {},
): UseNudgeLocationReturn {
  const { useExperimentIsOn } = useExperiment();
  const nudgesAreEnabled = useExperimentIsOn("nudges");
  const analytics = useAnalytics();
  const { client } = useContext(AppContext);
  const clientId = client?.uuid || client?.client_uuid;

  const {
    minViewTime = 1000,
    threshold = 0.5,
    trackOnMount = false,
    disableProductAnalytics = false,
    queryOptions = {},
  } = options;

  const [nudgeStates, setNudgeStates] = useState<
    Record<string, { hasTrackedImpression: boolean; isDismissed: boolean }>
  >({});

  const observersRef = useRef<Record<string, IntersectionObserver>>({});
  const timeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});
  const elementsRef = useRef<Record<string, HTMLDivElement | null>>({});
  const impressionTrackedRef = useRef<Record<string, boolean>>({});

  const {
    data: fetchedNudges = [],
    isLoading,
    error,
  } = useQuery<Nudge[]>({
    queryKey: ["nudges-location", clientId, location],
    enabled: nudgesAreEnabled,
    queryFn: () => getNudgesByLocation(location),
    ...DEFAULT_NUDGE_QUERY_OPTIONS,
    ...queryOptions,
  });

  useEffect(() => {
    if (fetchedNudges.length === 0) {
      return;
    }

    setNudgeStates((prev) => {
      const newState = { ...prev };
      let hasChanges = false;

      fetchedNudges.forEach((nudge) => {
        if (!newState[nudge.key]) {
          newState[nudge.key] = {
            hasTrackedImpression: false,
            isDismissed: false,
          };
          hasChanges = true;
        }
        if (impressionTrackedRef.current[nudge.key] === undefined) {
          impressionTrackedRef.current[nudge.key] = false;
        }
      });

      return hasChanges ? newState : prev;
    });
  }, [fetchedNudges.length, fetchedNudges]);

  useEffect(() => {
    if (!trackOnMount || fetchedNudges.length === 0) return;

    fetchedNudges.forEach((nudge) => {
      if (!impressionTrackedRef.current[nudge.key]) {
        impressionTrackedRef.current[nudge.key] = true;

        trackNudgeInteraction(nudge.key, "impression")
          .then(() => {
            setNudgeStates((prev) => ({
              ...prev,
              [nudge.key]: {
                ...prev[nudge.key],
                hasTrackedImpression: true,
              },
            }));

            if (!disableProductAnalytics) {
              analytics.trackEvent(NUDGE_EVENT.IMPRESSION, {
                nudge_key: nudge.key,
                nudge_location: location,
                nudge_priority: nudge.priority,
              });
            }
          })
          .catch((error) => {
            console.warn("Failed to track nudge impression:", error);
            impressionTrackedRef.current[nudge.key] = false;
          });
      }
    });
  }, [
    trackOnMount,
    fetchedNudges.length,
    fetchedNudges,
    location,
    disableProductAnalytics,
    analytics,
  ]);

  const createTrackingFunctions = useCallback(
    (nudge: Nudge) => {
      const trackImpression = async () => {
        if (impressionTrackedRef.current[nudge.key]) return;

        impressionTrackedRef.current[nudge.key] = true;

        try {
          await trackNudgeInteraction(nudge.key, "impression");

          setNudgeStates((prev) => ({
            ...prev,
            [nudge.key]: {
              ...prev[nudge.key],
              hasTrackedImpression: true,
            },
          }));

          if (!disableProductAnalytics) {
            analytics.trackEvent(NUDGE_EVENT.IMPRESSION, {
              nudge_key: nudge.key,
              nudge_location: location,
              nudge_priority: nudge.priority,
            });
          }
        } catch (error) {
          console.warn("Failed to track nudge impression:", error);
          impressionTrackedRef.current[nudge.key] = false;
        }
      };

      const trackClick = async () => {
        try {
          await trackNudgeInteraction(nudge.key, "click");

          if (!disableProductAnalytics) {
            analytics.trackEvent(NUDGE_EVENT.CLICK, {
              nudge_key: nudge.key,
              nudge_location: location,
              nudge_priority: nudge.priority,
            });
          }
        } catch (error) {
          console.warn("Failed to track nudge click:", error);
        }
      };

      const trackDismiss = async () => {
        try {
          await dismissNudge(nudge.key);
          setNudgeStates((prev) => ({
            ...prev,
            [nudge.key]: {
              ...prev[nudge.key],
              isDismissed: true,
            },
          }));

          if (!disableProductAnalytics) {
            analytics.trackEvent(NUDGE_EVENT.DISMISS, {
              nudge_key: nudge.key,
              nudge_location: location,
              nudge_priority: nudge.priority,
            });
          }
        } catch (error) {
          console.warn("Failed to track nudge dismiss:", error);
        }
      };

      const trackingRef = (element: HTMLDivElement | null) => {
        const nudgeKey = nudge.key;
        elementsRef.current[nudgeKey] = element;

        if (observersRef.current[nudgeKey]) {
          observersRef.current[nudgeKey].disconnect();
          delete observersRef.current[nudgeKey];
        }

        if (timeoutsRef.current[nudgeKey]) {
          clearTimeout(timeoutsRef.current[nudgeKey]);
          delete timeoutsRef.current[nudgeKey];
        }

        if (!element || trackOnMount) {
          return;
        }

        observersRef.current[nudgeKey] = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;

            if (entry.isIntersecting) {
              timeoutsRef.current[nudgeKey] = setTimeout(() => {
                if (!impressionTrackedRef.current[nudgeKey]) {
                  trackImpression();
                }
              }, minViewTime);
            } else {
              if (timeoutsRef.current[nudgeKey]) {
                clearTimeout(timeoutsRef.current[nudgeKey]);
                delete timeoutsRef.current[nudgeKey];
              }
            }
          },
          { threshold },
        );

        observersRef.current[nudgeKey].observe(element);
      };

      return {
        trackClick,
        trackDismiss,
        trackingRef,
        trackImpression,
      };
    },
    [
      location,
      minViewTime,
      threshold,
      trackOnMount,
      disableProductAnalytics,
      analytics,
    ],
  );

  useEffect(() => {
    return () => {
      Object.values(observersRef.current).forEach((observer) => {
        observer.disconnect();
      });
      Object.values(timeoutsRef.current).forEach((timeout) => {
        clearTimeout(timeout);
      });
      observersRef.current = {};
      timeoutsRef.current = {};
      impressionTrackedRef.current = {};
    };
  }, []);

  const nudges: NudgeWithTracking[] = fetchedNudges.map((nudge) => {
    const state = nudgeStates[nudge.key] || {
      hasTrackedImpression: false,
      isDismissed: false,
    };
    const { trackClick, trackDismiss, trackingRef } =
      createTrackingFunctions(nudge);

    return {
      nudge,
      trackClick,
      trackDismiss,
      trackingRef,
      hasTrackedImpression:
        state.hasTrackedImpression ||
        impressionTrackedRef.current[nudge.key] ||
        false,
      isDismissed: state.isDismissed,
    };
  });

  return {
    nudges,
    isLoading,
    error,
  };
}
